'use client';

import {
  CheckCircle2,
  Clock,
  FileAudio,
  Globe,
  Loader2,
  Mic,
  RotateCcw,
  Search,
  Trash2,
  Users,
  XCircle,
} from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { useCapability } from '@/features/billing';
import { cn } from '@/lib/utils';
import { formatCreditCost } from '@/features/usage/format';
import { useLocale } from '@/lib/i18n/useLocale';
import type { Recording } from '@/lib/protobuf/gen/recordings/recordings_pb';
import { failureReasonLabel } from './failureReason';
import { Highlight } from './Highlight';
import { showsSpinner, statusBadgeLabel } from './statusLabels';
import {
  canCancelTranscription,
  STATUS_CANCELLED,
  useRecordingsList,
  useRecordingsStore,
} from './recordingsStore';
import { filterRecordings, transcriptExcerpt } from './searchRecordings';
import { transcriptSnippet } from './transcriptSnippet';

function formatDuration(secs: number): string {
  if (!Number.isFinite(secs) || secs <= 0) return '0:00';
  const total = Math.floor(secs);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m}:${s.toString().padStart(2, '0')}`;
}

/** Proto-Timestamp → short date for the list. Today shows time; older shows date. */
function formatRecordedAt(ts: { seconds: bigint; nanos: number }): string {
  const d = new Date(Number(ts.seconds) * 1000);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString();
}

function statusBadgeVariant(
  status: string,
): 'default' | 'running' | 'completed' | 'failed' | 'queued' {
  switch (status) {
    case 'ready':
    case 'completed':
      return 'completed';
    case 'transcribing':
    case 'extracting':
    case 'processing':
    case 'uploading':
      return 'running';
    case 'failed':
    case 'error':
      return 'failed';
    case 'pending':
    case 'queued':
      return 'queued';
    default:
      return 'default';
  }
}

/** Honest in-progress hint for the metadata line of an in-flight row. */
function progressHint(status: string, t: ReturnType<typeof useTranslations>): string {
  switch (status) {
    case 'uploading':
      return t('recordingList.progressUploading');
    case 'pending':
    case 'queued':
      return t('recordingList.progressQueued');
    default:
      return t('recordingList.progressTranscribing');
  }
}

export type RecordingView = 'cards' | 'rows';

interface RecordingListProps {
  className?: string;
  /** Per-item layout. Defaults to the dense `'rows'` layout. */
  view?: RecordingView;
  /** Case-insensitive filter over title, filename, speakers, and transcript. */
  query?: string | undefined;
  selectedId?: string | undefined;
  onSelect?: ((rec: Recording) => void) | undefined;
  onScrollToUpload?: (() => void) | undefined;
}

/** Distinct-speaker count: explicit `speakers` first, else derive from segments.
 * `speakers` is optional on the wire — proto-JSON (the upload REST response) omits
 * empty repeated fields, so a brand-new recording arrives with `speakers`
 * undefined. Guard with `?.` (do NOT assume an array). */
function speakerCount(rec: Recording): number {
  return (
    (rec.speakers?.length ?? 0) ||
    new Set(rec.transcript?.segments?.map((s) => s.speaker).filter(Boolean)).size ||
    0
  );
}

function RecordingList({
  className,
  view = 'rows',
  query,
  selectedId,
  onSelect,
  onScrollToUpload,
}: RecordingListProps) {
  const t = useTranslations('recordings');
  const recordings = useRecordingsList();
  const trimmedQuery = query?.trim() ?? '';
  const visible = React.useMemo(
    () => filterRecordings(recordings, trimmedQuery),
    [recordings, trimmedQuery],
  );
  const locale = useLocale();
  const loadState = useRecordingsStore((s) => s.loadState);
  const errorMessage = useRecordingsStore((s) => s.errorMessage);
  const fetchList = useRecordingsStore((s) => s.actions.fetchList);
  const destroy = useRecordingsStore((s) => s.actions.destroy);
  const retry = useRecordingsStore((s) => s.actions.retry);
  // BILL-20f: below Standard the server refuses RetryTranscription (241).
  const asrLocked = useCapability('recordings_asr').entitlement === 'locked';
  const cancelTranscription = useRecordingsStore((s) => s.actions.cancelTranscription);
  // The Set identity changes on every add/delete (immer), so a plain subscribe
  // is enough — no useShallow needed, and `has` is O(1) per row.
  const cancelPending = useRecordingsStore((s) => s.cancelPending);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  if (loadState === 'loading' && recordings.length === 0) {
    return (
      <div className={cn('space-y-3', className)}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={`rec-skel-${i.toString()}`}
            className="flex items-center gap-4 rounded-md border border-border p-4"
          >
            <Skeleton className="h-5 w-5" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-1/4" />
            </div>
            <Skeleton className="h-6 w-16" />
          </div>
        ))}
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div className={cn('rounded-md border border-destructive/40 bg-destructive/5 p-4', className)}>
        <p className="text-sm text-destructive">
          {errorMessage || t('recordingList.loadError')}
        </p>
        <Button variant="ghost" size="sm" className="mt-2" onClick={() => fetchList()}>
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> {t('recordingList.retry')}
        </Button>
      </div>
    );
  }

  if (recordings.length === 0) {
    return (
      <div
        className={cn('flex flex-col items-center gap-3 py-16', className)}
        data-testid="recordings-empty"
      >
        <Mic className="h-12 w-12 text-muted-foreground" />
        <p className="font-serif text-base text-foreground">{t('recordingList.emptyTitle')}</p>
        <p className="text-sm text-muted-foreground max-w-md text-center">
          {t('recordingList.emptyBody')}
        </p>
        {onScrollToUpload && (
          <Button size="sm" variant="ghost" onClick={onScrollToUpload}>
            {t('recordingList.startRecording')}
          </Button>
        )}
      </div>
    );
  }

  if (visible.length === 0 && trimmedQuery) {
    return (
      <div
        className={cn('flex flex-col items-center gap-2 py-16', className)}
        data-testid="recordings-no-matches"
      >
        <Search className="h-10 w-10 text-muted-foreground/50" />
        <p className="font-serif text-base text-foreground">{t('recordingList.noMatchesTitle')}</p>
        <p className="text-sm text-muted-foreground max-w-md text-center">
          {t('recordingList.noMatchesBody', { query: trimmedQuery })}
        </p>
      </div>
    );
  }

  return (
    <ul className={cn('space-y-2', className)}>
      {visible.map((rec) => {
        const isSelected = selectedId === rec.recordingId;
        const showSpinner = showsSpinner(rec.status);
        const isFailed = rec.status === 'failed' || rec.status === 'error';
        const isReady = rec.status === 'ready' || rec.status === 'completed';
        const isCancelled = rec.status === STATUS_CANCELLED;
        // The three server-accepted cancel statuses — NOT `showSpinner`, which
        // also covers the client-local `uploading`.
        const canCancel = canCancelTranscription(rec.status);
        // A cancelled recording is retryable: HandleRetryTranscription accepts
        // every terminal state (ready / failed / cancelled).
        const canRetry = isFailed || isCancelled;
        const label = rec.title || rec.originalName || rec.recordingId;

        // NOTE: this ternary chain — not `statusBadgeVariant` — is what decides
        // whether a status renders a badge at all. A status that matches none of
        // these branches falls through to `null` and renders nothing.
        const statusBadge = showSpinner ? (
          <Badge variant={statusBadgeVariant(rec.status)}>
            <Loader2 className="h-3 w-3 mr-1 animate-spin" aria-hidden />{' '}
            {statusBadgeLabel(rec.status, t)}
          </Badge>
        ) : isReady ? (
          <Badge variant="completed">{t('recordingList.statusReady')}</Badge>
        ) : isFailed ? (
          <Badge variant="failed">{t('recordingList.statusFailed')}</Badge>
        ) : isCancelled ? (
          // `queued` is bg-muted/text-muted-foreground — genuinely neutral.
          // NOT `paused`, which is byte-identical to `warning` (amber) and is
          // the de-facto caution colour elsewhere in the app; a cancel is a
          // deliberate user action, neither an error nor a warning.
          <Badge variant="queued">{t('recordingList.statusCancelled')}</Badge>
        ) : null;

        // The row is non-optimistic, so `status` does not move for the whole
        // round-trip: without this the button renders identically after the
        // click and the user clicks again, producing a request the server
        // refuses. `shrink-0` matches the sibling Delete button — Button's base
        // carries no `whitespace-nowrap`, so on a narrow viewport the label
        // would otherwise wrap inside a fixed-height box.
        const cancelInFlight = cancelPending.has(rec.recordingId);
        const cancelButton = canCancel ? (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            disabled={cancelInFlight}
            onClick={() => cancelTranscription(rec.recordingId)}
            aria-label={t('recordingList.cancelLabel', { label })}
          >
            {cancelInFlight ? (
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden />
            ) : (
              <XCircle className="h-3.5 w-3.5 mr-1" />
            )}{' '}
            {t('recordingList.cancel')}
          </Button>
        ) : null;

        const retryButton = canRetry ? (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => retry(rec.recordingId)}
            disabled={asrLocked}
            title={asrLocked ? t('recordingList.retryPlanLocked') : undefined}
            aria-label={t('recordingList.retryLabel', { label })}
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> {t('recordingList.retry')}
          </Button>
        ) : null;

        // BILL-05: machine sentinels (credits_exhausted, …) render as catalog
        // copy; the credits one carries an "Add credits" link, rendered as a
        // SIBLING after the row <button> — never inside it (invalid nesting,
        // and a click would open the row instead of /billing).
        const failure = isFailed ? failureReasonLabel(rec.failureReason, (k) => t(k as never)) : null;
        const topUpLink = failure?.topUp ? (
          <Link
            href="/billing"
            className="inline-flex shrink-0 items-center rounded-sm text-xs font-medium text-accent underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('failure.topUp')}
          </Link>
        ) : null;

        if (view === 'cards') {
          const snippet = isReady
            ? trimmedQuery
              ? transcriptExcerpt(rec, trimmedQuery)
              : transcriptSnippet(rec)
            : '';
          const speakers = speakerCount(rec);
          const language = rec.transcript?.language ?? '';
          return (
            <li key={rec.recordingId}>
              <div
                className={cn(
                  'relative rounded-xl border bg-surface p-3.5 transition-colors duration-150',
                  isSelected
                    ? 'bg-accent/5 border-accent/40'
                    : 'border-border hover:bg-hover',
                )}
              >
                <button
                  type="button"
                  onClick={() => onSelect?.(rec)}
                  className="block w-full min-w-0 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={t('recordingList.openLabel', { label })}
                >
                  <div className="flex items-center gap-2">
                    <FileAudio className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                      <Highlight text={label} query={trimmedQuery} />
                    </span>
                    {statusBadge}
                  </div>

                  {showSpinner ? (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                      {progressHint(rec.status, t)}
                    </p>
                  ) : isFailed ? (
                    failure && <p className="mt-2 text-xs text-destructive">{failure.text}</p>
                  ) : isCancelled ? (
                    // Terminal, but never finished: DurationSecs and Speakers
                    // are only ever written from the ASR worker result
                    // (orchestrator.go), so the branch below would print
                    // "0:00" and "0 speakers" — the same "reads like a broken
                    // job" failure the in-flight case twelve lines up already
                    // avoids.
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t('recordingList.progressCancelled')}
                    </p>
                  ) : (
                    <>
                      {snippet && (
                        <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
                          <Highlight text={snippet} query={trimmedQuery} />
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <span className="text-xs text-muted-foreground bg-muted/50 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" aria-hidden />
                          {formatDuration(rec.durationSecs)}
                        </span>
                        <span className="text-xs text-muted-foreground bg-muted/50 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <Users className="h-3 w-3" aria-hidden />
                          {t('recordingList.speakers', { count: speakers })}
                        </span>
                        {language && (
                          <span className="text-xs text-muted-foreground bg-muted/50 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                            <Globe className="h-3 w-3" aria-hidden />
                            {language}
                          </span>
                        )}
                      </div>
                      {rec.promotedDocId && (
                        <p className="mt-2 inline-flex items-center gap-1 text-xs text-accent">
                          <CheckCircle2 className="h-3.5 w-3.5" /> {t('recordingList.inDocuments')}
                        </p>
                      )}
                    </>
                  )}
                </button>
                {topUpLink && <div className="mt-1.5">{topUpLink}</div>}

                <div className="absolute right-2 top-2 flex items-center gap-1">
                  {cancelButton}
                  {retryButton}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 shrink-0 p-0"
                    onClick={() => destroy(rec.recordingId)}
                    aria-label={t('recordingList.deleteLabel', { label })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </li>
          );
        }

        return (
          <li key={rec.recordingId}>
            <div
              className={cn(
                'flex items-center gap-4 rounded-md border border-border p-4 transition-colors duration-150',
                isSelected ? 'bg-accent/5 border-accent/40' : 'hover:bg-hover',
              )}
            >
              <button
                type="button"
                onClick={() => onSelect?.(rec)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
                aria-label={t('recordingList.openLabel', { label })}
              >
                <FileAudio className="h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    <Highlight text={label} query={trimmedQuery} />
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {rec.createdAt && (
                      <>
                        <span>{formatRecordedAt(rec.createdAt)}</span>
                        <span aria-hidden> · </span>
                      </>
                    )}
                    {showSpinner ? (
                      // Duration + cost are zero until the server finishes, so
                      // showing "0:00 · ≈ 0 credits" reads like a broken job. Give
                      // an honest in-progress hint instead.
                      <span>{progressHint(rec.status, t)}</span>
                    ) : isCancelled ? (
                      // Same reason, terminal case: a cancelled recording never
                      // reached the worker result that writes DurationSecs.
                      <span>{t('recordingList.progressCancelled')}</span>
                    ) : (
                      <>
                        <span>{formatDuration(rec.durationSecs)}</span>
                        {/* Self-hosted ASR is free unless ops sets a per-minute
                            rate, so cost is 0 for most recordings. Omit it then —
                            "≈ 0 credits" reads like a broken job. */}
                        {Number(rec.costCredits) > 0 && (
                          <>
                            <span aria-hidden> · </span>
                            <span>
                              {t('recordingList.approxCost', {
                                cost: formatCreditCost(Number(rec.costCredits), locale),
                              })}
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </p>
                  {failure && <p className="mt-1 text-xs text-destructive truncate">{failure.text}</p>}
                </div>
              </button>

              {topUpLink}

              {statusBadge}
              {rec.promotedDocId && (
                <span
                  className="inline-flex items-center gap-1 text-xs text-accent"
                  title={t('recordingList.inDocumentsTitle')}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                </span>
              )}
              {cancelButton}
              {retryButton}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 shrink-0 p-0"
                onClick={() => destroy(rec.recordingId)}
                aria-label={t('recordingList.deleteLabel', { label })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export { RecordingList };
